import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    FiAlertTriangle, FiArrowDown, FiArrowUp,
    FiChevronDown, FiChevronLeft, FiChevronRight,
    FiChevronsLeft, FiChevronsRight, FiDownload,
    FiFileText, FiHash, FiPlus,
    FiRefreshCw, FiSearch, FiTrash2, FiX,
} from "react-icons/fi";
import {
    CompanyStatusBanner, DeleteModal, DocStatusBadge,
    PreviewPanel, SearchTypeDropdown, SortTh,
    StatCard, T, fmtMYR, fmtCurrency,
} from "./SalesShared";
import { companyProfileService } from "../../_services/companyProfile.service";
import "../../Assets/Accounting.css";


// HELPERS
function useDebounce(value, ms = 300) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return debounced;
}

/**
 * Resolve the raw (string/number) value from a row for a given column.
 * Used for Excel export — skips JSX render output.
 */
function getRawValue(row, col) {
    const raw = row[col.key];
    if (!col.render) return raw ?? "";
    try {
        const rendered = col.render(raw, row);
        return typeof rendered === "string" || typeof rendered === "number" ? rendered : raw ?? "";
    } catch {
        return raw ?? "";
    }
}

/** Export visible rows to an xlsx file. Lazy-loads the xlsx library. */
async function exportToExcel(rows, docType, columns) {
    try {
        const XLSX = await import("xlsx").catch(() => null);
        if (!XLSX) {
            alert("Excel export requires the 'xlsx' package.\nRun: npm install xlsx");
            return;
        }
        const data = rows.map((r) => {
            const obj = {};
            columns.forEach((c) => { obj[c.label] = getRawValue(r, c); });
            return obj;
        });
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, docType);
        XLSX.writeFile(wb, `${docType}-List-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
        console.error("Export error:", err);
        alert("Export failed. Please try again.");
    }
}

/** Styled pagination button. */
const pagBtnStyle = (disabled) => ({
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 28, height: 28, borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: disabled ? "transparent" : "#fff",
    color: disabled ? T.textLight : T.textMuted,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
    transition: "all 0.15s",
});

// PAGINATION SELECT
function PagSelect({ value, onChange, options }) {
    const [focused, setFocused] = useState(false);
    return (
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
            <select
                value={value}
                onChange={onChange}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                style={{
                    height: 28, padding: "0 26px 0 8px", fontSize: 13,
                    border: `1px solid ${focused ? T.accent : T.border}`,
                    borderRadius: 6, background: "#fff", color: T.textPrimary,
                    outline: "none", cursor: "pointer",
                    appearance: "none", boxSizing: "border-box",
                    transition: "border-color 0.15s", fontFamily: "inherit",
                }}
            >
                {options.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <FiChevronDown
                size={12}
                style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", color: T.textLight, pointerEvents: "none" }}
            />
        </div>
    );
}


// MAIN COMPONENT
export default function SalesDocList({
    docType,
    basePath,
    fetchList,
    fetchKpis,
    deleteItem,
    statuses = ["All"],
    kpiCards = [],
    previewFields = [],
    customColumns = [],
    hideCreate = false,
    viewOnly = false,
    hideExport = false,
    searchOptions = [],
    showFromQuotation = false,
    renderPreviewActions = null,
}) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // ── URL-synced filter state ─────────────────────────────────────────────

    const searchInput_url = searchParams.get("search") || "";
    const searchType = searchParams.get("searchType") || "all";
    const statusFilter = searchParams.get("status") || "All";
    const sortBy = searchParams.get("sortBy") || "";
    const sortDir = searchParams.get("sortDir") || "";
    const page = Number(searchParams.get("page") || 1);
    const pageSize = Number(searchParams.get("pageSize") || 10);

    /** Update a single URL param; resets page to 1 for filter changes. */
    const setParam = useCallback((key, val) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(key, val);
            if (key !== "page") next.set("page", "1");
            return next;
        });
    }, [setSearchParams]);

    // ── Local state ─────────────────────────────────────────────────────────

    const [searchInput, setSearchInput] = useState(searchInput_url);
    const [rows, setRows] = useState([]);
    const [pagination, setPag] = useState({ total: 0, page: 1, pageSize: 10 });
    const [kpis, setKpis] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingKpi, setLoadingKpi] = useState(true);
    const [error, setError] = useState("");
    const [retryKey, setRetryKey] = useState(0);
    const [selected, setSelected] = useState(null);
    const [deleteTgt, setDeleteTgt] = useState(null);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const [typeOpen, setTypeOpen] = useState(false);
    const [companyProfile, setCP] = useState(null);
    const [exporting, setExporting] = useState(false);

    const typeAnchorRef = useRef(null);
    const queryGenerationRef = useRef(0);
    const deleteInFlightRef = useRef(false);
    const isInactive = !!(companyProfile && companyProfile.Status !== "Active");
    const debSearch = useDebounce(searchInput);

    // ── Sync debounced search input → URL ───────────────────────────────────

    useEffect(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (debSearch) next.set("search", debSearch); else next.delete("search");
            next.set("page", "1");
            return next;
        });
    }, [debSearch, setSearchParams]);

    // ── Memoised query params passed to API ─────────────────────────────────

    const qp = useMemo(() => ({
        search: searchInput_url, searchType, status: statusFilter,
        sortBy, sortDir, page, pageSize,
    }), [searchInput_url, searchType, statusFilter, sortBy, sortDir, page, pageSize]);

    // ── Fetch rows ──────────────────────────────────────────────────────────

    useEffect(() => {
        let alive = true;
        const requestGeneration = ++queryGenerationRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError("");

        const { page, pageSize, status, sortBy, sortDir, search, searchType } = qp;
        const params = { page, pageSize, status, sortBy, sortDir };
        if (search) { params.search = search; params.searchType = searchType; }

        fetchList(params, { signal: controller.signal })
            .then(({ data, pagination: pg }) => {
                if (!alive || requestGeneration !== queryGenerationRef.current) return;
                setRows(data);
                setPag(pg);
            })
            .catch((error) => {
                if (alive && error.name !== "AbortError") {
                    setError(`Failed to load ${docType.toLowerCase()}s.`);
                }
            })
            .finally(() => { if (alive) setLoading(false); });

        return () => {
            alive = false;
            controller.abort();
        };
    }, [fetchList, JSON.stringify(qp), docType, retryKey]);

    // ── Fetch KPIs ──────────────────────────────────────────────────────────

    useEffect(() => {
        if (!fetchKpis) return;
        setLoadingKpi(true);
        fetchKpis()
            .then(setKpis)
            .catch(() => { })
            .finally(() => setLoadingKpi(false));
    }, [fetchKpis]);

    // ── Company profile (for inactive banner) ───────────────────────────────

    useEffect(() => {
        companyProfileService.getProfile().then(setCP).catch(() => { });
    }, []);

    // ── Handlers ────────────────────────────────────────────────────────────

    const handleSort = (col) => {
        const newDir = sortBy === col && sortDir === "asc" ? "desc" : "asc";
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("sortBy", col);
            next.set("sortDir", newDir);
            next.set("page", "1");
            return next;
        });
    };

    const handleReset = () => {
        setSearchInput("");
        setSearchParams({ page: "1", pageSize: String(pageSize) });
    };

    /**
     * "Export Excel" should export every row matching the current filters,
     * not just the page currently on screen — so re-fetch with the same
     * filters/sort but a page size big enough to cover them all first.
     */
    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const { search, searchType, status, sortBy, sortDir } = qp;
            const params = { page: 1, pageSize: Math.max(pagination.total, 1), status, sortBy, sortDir };
            if (search) { params.search = search; params.searchType = searchType; }
            const { data } = await fetchList(params);
            await exportToExcel(data, docType, allColumns);
        } catch (err) {
            console.error("Export fetch error:", err);
            alert("Export failed. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTgt || deleteInFlightRef.current) return;
        if (isInactive) {
            alert("Action restricted. Your company status is currently Inactive.");
            return;
        }
        const previousRows = rows;
        const previousPagination = pagination;
        const previousKpis = kpis;
        const previousSelected = selected;
        const target = deleteTgt;
        const mutationGeneration = queryGenerationRef.current;
        const statusKey = target.status === "Pending for Approval"
            ? "pending"
            : String(target.status || "").trim().toLowerCase().replace(/\s+/g, "");
        deleteInFlightRef.current = true;
        setDeleteInProgress(true);
        setRows((current) => current.filter((row) => row.id !== target.id));
        setPag((current) => ({ ...current, total: Math.max(0, current.total - 1) }));
        setKpis((current) => ({
            ...current,
            ...(Object.prototype.hasOwnProperty.call(current, "total")
                ? { total: Math.max(0, (current.total || 0) - 1) }
                : {}),
            ...(statusKey && Object.prototype.hasOwnProperty.call(current, statusKey)
                ? { [statusKey]: Math.max(0, (current[statusKey] || 0) - 1) }
                : {}),
        }));
        if (selected?.id === target.id) setSelected(null);
        setDeleteTgt(null);
        try {
            if (deleteItem) await deleteItem(target.id);
            // Refresh approval badge if a pending doc was deleted
            if (target.status === "Pending for Approval") {
                window.dispatchEvent(new CustomEvent("refreshPendingApprovalCounts"));
            }
            if (mutationGeneration !== queryGenerationRef.current) {
                setRetryKey((current) => current + 1);
                return;
            }
            const nextTotal = Math.max(0, previousPagination.total - 1);
            const lastPage = Math.max(1, Math.ceil(nextTotal / pageSize));
            if (mutationGeneration === queryGenerationRef.current && page > lastPage) setParam("page", String(lastPage));
            if (fetchKpis) {
                try {
                    const refreshedKpis = await fetchKpis();
                    if (mutationGeneration === queryGenerationRef.current) setKpis(refreshedKpis);
                } catch {
                    // Preserve the optimistic values until the next normal refresh.
                }
            }
        } catch {
            if (mutationGeneration === queryGenerationRef.current) {
                setRows(previousRows);
                setPag(previousPagination);
                setKpis(previousKpis);
                setSelected(previousSelected);
                setError(`Failed to delete ${docType.toLowerCase()}.`);
            }
        } finally {
            deleteInFlightRef.current = false;
            setDeleteInProgress(false);
        }
    };

    // ── Derived display values ───────────────────────────────────────────────

    const totalPages = Math.max(1, Math.ceil(pagination.total / pageSize));
    const startIdx = (page - 1) * pageSize;
    const currentSearchLabel = searchOptions.find((o) => o.key === searchType)?.label || "All";
    const goToPage = (p) => setParam("page", String(Math.max(1, Math.min(p, totalPages))));

    // ── Column definitions ───────────────────────────────────────────────────

    const allColumns = useMemo(() => {
        // If the caller supplied custom columns, use them
        if (customColumns.length > 0) return customColumns;

        // Default column set — covers most document types
        return [
            {
                label: `${docType} No.`, key: "id", width: 110,
                render: (v) => <span style={{ color: T.textLight, fontWeight: 700, fontSize: 13 }}>{v}</span>,
            },
            {
                label: "Date", key: "date", width: 110,
                render: (v) => <span style={{ color: T.textMuted, fontSize: 13 }}>{v}</span>,
            },
            {
                label: "Contact Person", key: "contactPerson", width: 220,
                render: (v) => (
                    <span style={{ fontWeight: 600, display: "inline-block", maxWidth: 210, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {v}
                    </span>
                ),
            },
            {
                label: "Company Name", key: "customer", width: 180,
                render: (v, row) => (
                    <span style={{ color: T.textMuted, fontSize: 13, display: "inline-block", maxWidth: 170, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {row.customer || row.supplier || "—"}
                    </span>
                ),
            },
            {
                label: "Grand Total", key: "grandTotal",
                render: (v, row) => <span style={{ fontWeight: 700, color: T.textPrimary }}>{fmtCurrency(v, row.currency)}</span>,
            },
            {
                label: "Status", key: "status",
                render: (v) => <DocStatusBadge value={v} />,
            },
        ];
}, [docType, customColumns]);

// ── Render ───────────────────────────────────────────────────────────────

return (
    <div style={{ padding: "20px 0" }} onClick={() => setTypeOpen(false)}>

        {/* Page header */}
        <div style={{ marginBottom: 18 }}>
            <h4 style={{ fontWeight: 700, color: T.textPrimary, margin: 0 }}>{docType} List</h4>
            <p style={{ fontSize: 13, color: T.textMuted, margin: "3px 0 0" }}>
                Manage your {docType.toLowerCase()} records. Click a row to preview.
            </p>
        </div>

        <CompanyStatusBanner isInactive={isInactive} onNavigate={() => navigate("/contacts/company")} />

        {/* KPI cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 20 }}>
            {kpiCards.map(({ label, key, icon, color }) => (
                <StatCard key={key} icon={icon} color={color} label={label} value={loadingKpi ? "…" : kpis[key] ?? 0} />
            ))}
        </div>

        {/* Status filter pills */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
            {statuses.map((s) => (
                <button
                    key={s}
                    onClick={() => setParam("status", s)}
                    style={{
                        padding: "5px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                        cursor: "pointer", border: "1.5px solid",
                        borderColor: statusFilter === s ? T.accent : T.border,
                        background: statusFilter === s ? "#eff6ff" : "#fff",
                        color: statusFilter === s ? T.accent : T.textMuted,
                        transition: "all 0.15s",
                    }}
                >
                    {s}
                </button>
            ))}
        </div>

        {/* Table card + preview panel */}
        <div className="cl-content-wrapper">
            <div
                className="cl-table-container"
                style={{ flex: 1, minWidth: 0, background: T.cardBg, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}
            >
                {/* Toolbar */}
                <div className="cl-toolbar-sticky">
                    {/* Search input */}
                    <div className="cl-search-bar" style={{ flex: 1, minWidth: 220, maxWidth: 480 }}>
                        <FiSearch size={14} style={{ margin: "auto 8px auto 10px", color: T.textLight, flexShrink: 0 }} />
                        <input
                            className="cl-search-input"
                            placeholder={`Search by ${currentSearchLabel}…`}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                        <div className="cl-search-divider" />
                        <div className="cl-search-type-wrap" ref={typeAnchorRef} onClick={(e) => e.stopPropagation()}>
                            <button type="button" className="cl-search-type-btn" onClick={() => setTypeOpen((o) => !o)}>
                                {currentSearchLabel}
                            </button>
                            <FiChevronDown size={13} className={`cl-search-chevron${typeOpen ? " open" : ""}`} />
                            <SearchTypeDropdown
                                anchorRef={typeAnchorRef}
                                open={typeOpen}
                                current={searchType}
                                options={searchOptions}
                                onSelect={(k) => { setParam("searchType", k); setTypeOpen(false); }}
                            />
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                        <button className="cl-btn-reset" onClick={handleReset}>
                            <FiRefreshCw size={13} /> Reset Filter
                        </button>

                        {!hideExport && (
                            <button
                                onClick={handleExport}
                                disabled={exporting || pagination.total === 0}
                                title={pagination.total === 0 ? "No rows to export" : "Export all rows matching the current filters"}
                                style={{
                                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                                    height: 40, padding: "0 13px", boxSizing: "border-box", borderRadius: T.radiusSm,
                                    border: "1.5px solid #16a34a", background: "#f0fdf4", color: "#15803d",
                                    fontSize: 13, fontWeight: 600,
                                    cursor: exporting || pagination.total === 0 ? "not-allowed" : "pointer",
                                    opacity: exporting || pagination.total === 0 ? 0.6 : 1,
                                }}
                            >
                                <FiDownload size={13} /> {exporting ? "Exporting…" : "Export Excel"}
                            </button>
                        )}

                        {showFromQuotation && !hideCreate && !viewOnly && (
                            <button
                                onClick={() => navigate(`${basePath}/add?fromQuotation=true`)}
                                disabled={isInactive}
                                style={{
                                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                                    height: 40, padding: "0 13px", boxSizing: "border-box",
                                    borderRadius: T.radiusSm,
                                    border: `1.5px solid ${isInactive ? T.border : T.accent}`,
                                    background: isInactive ? T.bg : "#eff6ff",
                                    color: isInactive ? T.textLight : T.accent,
                                    fontSize: 13, fontWeight: 600,
                                    cursor: isInactive ? "not-allowed" : "pointer",
                                }}
                            >
                                <FiFileText size={14} /> From Quotation
                            </button>
                        )}

                        {!hideCreate && !viewOnly && (
                            <button
                                className="cl-btn-add"
                                onClick={() => navigate(`${basePath}/add`)}
                                disabled={isInactive}
                                style={{
                                    background: isInactive ? "#94a3b8" : T.accent,
                                    cursor: isInactive ? "not-allowed" : "pointer",
                                    opacity: isInactive ? 0.7 : 1,
                                }}
                            >
                                <FiPlus size={14} /> Create {docType}
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="cl-table-box">
                    <div className="cl-table-scroll">
                        <table className="cl-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}><FiHash size={11} /></th>
                                    {allColumns.map((col) => {
                                        // Grand total and DO shipping address are not sortable
                                        const nonSortable =
                                            col.key === "grandTotal" ||
                                            (docType === "Delivery Order" && col.key === "shippingAddress");

                                        return nonSortable ? (
                                            <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                                                {col.label}
                                            </th>
                                        ) : (
                                            <SortTh
                                                key={col.key}
                                                label={col.label}
                                                id={col.key}
                                                sortBy={sortBy}
                                                sortDir={sortDir}
                                                onSort={handleSort}
                                            />
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody style={{ opacity: loading && rows.length ? 0.55 : 1 }}>
                                {loading && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={allColumns.length + 1} className="cl-empty">Loading…</td>
                                    </tr>
                                )}
                                {!loading && error && (
                                    <tr>
                                        <td colSpan={allColumns.length + 1} className="cl-empty" style={{ color: T.danger }}>
                                            {error}{" "}
                                            <button type="button" onClick={() => setRetryKey((key) => key + 1)}>
                                                Retry
                                            </button>
                                        </td>
                                    </tr>
                                )}
                                {!loading && !error && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={allColumns.length + 1} className="cl-empty">
                                            No {docType.toLowerCase()}s found.
                                        </td>
                                    </tr>
                                )}
                                {!error && rows.map((row, idx) => (
                                    <tr
                                        key={row.id}
                                        className={`cl-table-row${selected?.id === row.id ? " selected" : ""}`}
                                        onClick={() => setSelected(row)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                setSelected(row);
                                            }
                                        }}
                                        tabIndex={0}
                                        aria-selected={selected?.id === row.id}
                                    >
                                        <td style={{ color: T.textLight, fontSize: 14 }}>{startIdx + idx + 1}</td>
                                        {allColumns.map((col) => (
                                            <td key={col.key} style={col.width ? { maxWidth: col.width } : undefined}>
                                                {col.render ? col.render(row[col.key], row) : row[col.key] || "—"}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                <div className="cl-pagination" onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: T.textMuted }}>
                        <span style={{ whiteSpace: "nowrap" }}>Rows per page:</span>
                        <PagSelect
                            value={pageSize}
                            onChange={(e) => setParam("pageSize", e.target.value)}
                            options={["5", "10", "20", "50"]}
                        />
                    </div>

                    <span style={{ fontSize: 13, color: T.textMuted, whiteSpace: "nowrap" }}>
                        {pagination.total === 0
                            ? "0–0 of 0"
                            : `${startIdx + 1}–${Math.min(startIdx + pageSize, pagination.total)} of ${pagination.total}`
                        }
                    </span>

                    <div style={{ display: "flex", gap: 2 }}>
                        <button onClick={() => goToPage(1)} disabled={page <= 1} style={pagBtnStyle(page <= 1)} title="First" aria-label="First page">    <FiChevronsLeft size={13} /></button>
                        <button onClick={() => goToPage(page - 1)} disabled={page <= 1} style={pagBtnStyle(page <= 1)} title="Previous" aria-label="Previous page"> <FiChevronLeft size={13} /></button>
                        <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages} style={pagBtnStyle(page >= totalPages)} title="Next" aria-label="Next page">     <FiChevronRight size={13} /></button>
                        <button onClick={() => goToPage(totalPages)} disabled={page >= totalPages} style={pagBtnStyle(page >= totalPages)} title="Last" aria-label="Last page">     <FiChevronsRight size={13} /></button>
                    </div>
                </div>
            </div>

            {/* Preview panel */}
            {selected && (
                <PreviewPanel
                    item={selected}
                    docType={selected._docType || docType}
                    previewFields={previewFields}
                    onClose={() => setSelected(null)}
                    onView={(id) => navigate(id.toString().includes("/") ? id : `${basePath}/${id}`)}
                    onDelete={(item) => {
                        if (!deleteInFlightRef.current) setDeleteTgt(item);
                    }}
                    renderPreviewActions={renderPreviewActions}
                    isInactive={isInactive}
                    viewOnly={viewOnly}
                    deleteDisabled={deleteInProgress}
                />
            )}
        </div>

        <DeleteModal
            target={deleteTgt}
            docType={docType}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeleteTgt(null)}
            busy={deleteInProgress}
        />
    </div>
);
}